import { useState, type RefObject } from 'react';
import type { TerraSignalRow } from '@shared/schemas/terra-signals';
import { ResponsivePopover } from '../ResponsivePopover';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';
import { parseBareCalendarDay } from '../../../lib/calendarDay';
import { useSnoozeSignal } from './useSignalsMutations';
import { hasSnoozeStepSignature } from './rowActions';

/**
 * The "Mark as read" popover (plan 2026-09-24-002 U6, bead .16; internal
 * names/files stay `Snooze*` — only the rep-visible label changed, per
 * Spencer's override on qa-batch6-plan.md). Opened by `SignalRowActions`'
 * single "Mark as read" button (there is no separate Done button — that was
 * qa-batch6-plan.md's original D1, superseded before it shipped). Five
 * options: "Indefinitely" — "Comes back when Gong moves to the next step" —
 * shown ONLY when the row carries a full Gong step signature
 * (`hasSnoozeStepSignature`, `rowActions.ts`), then four dated ones, always
 * shown. An inline `needs_date` recovery path, and a
 * terminal-error (409) state that disables every option rather than letting
 * the rep retry on a row the server has already closed out from under them.
 *
 * Wire contract (round-1 fix D-K): `snoozeUntil` is an ISO **instant** —
 * the moment the row un-snoozes — not a calendar day. Every option here
 * serialises the rep's own LOCAL midnight (`localMidnightIso`); a bare
 * `YYYY-MM-DD` would be read as UTC midnight server-side, which is already
 * in the past for a US rep and makes the snooze expire on arrival. The
 * server refuses any instant at or before `now()` with `code: 'past_date'`
 * (`src/api/flow-signals.ts`); that needs no branch here — like `archived`
 * it renders as title-then-detail in the same inline error box.
 *
 * Deliberately NO `useDraftState` (coding-standards.md §1, CLAUDE.md
 * "Draft State Under SSE"): this popover is only ever mounted by
 * `SignalRowActions` WHILE open (`{activePopover === 'snooze' && <SnoozePopover .../>}`)
 * — closing it unmounts the component, so every reopen starts from a fresh
 * `useState` initializer. There is no query-data reseed to guard against
 * (plan D18 / `terra/no-effect-reseeded-draft`).
 */

export type SnoozePopoverRow = Pick<TerraSignalRow, 'signal_id' | 'signal_instance_id' | 'step_number' | 'signal_tactic' | 'signal_type'>;

export interface SnoozePopoverProps {
  row: SnoozePopoverRow;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Fires on a successful snooze, before `onClose` — drives auto-expand-next. */
  onActed: (signalId: string) => void;
}

interface InlineError {
  code: string | undefined;
  title: string;
  detail: string | undefined;
  /** Salesforce re-auth dead end fix: set when the server's `ApiProblemError`
   *  carried an `oauthUrl` — renders a "Reconnect Salesforce" link instead of
   *  leaving the rep stuck on a 401 with no way forward. */
  oauthUrl: string | undefined;
}

/** Local-midnight "today", zeroed to calendar-day granularity — same discipline `prioritizeSignals.ts`/`arrangeSignalsClientSide.ts` already follow (never a raw `new Date()` read mid-comparison). */
function todayLocalMidnight(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addDays(base: Date, days: number): Date {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
}

/** `YYYY-MM-DD`, local calendar day — the native `<input type="date">` wire format. Used ONLY for the input's `min`, never for the request body (see `localMidnightIso`). */
function isoDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Round-1 fix D-K / R15 (write half). `snoozeUntil` is an INSTANT — the
 * moment the row comes back — not a calendar day. Sending a bare
 * `YYYY-MM-DD` made the server's `new Date(...)` read it as UTC midnight,
 * which for any negative-offset (US) rep is still *today* locally: picking
 * "Tomorrow" at 8pm ET produced an instant ~4 hours in the past, the snooze
 * expired immediately, and the row reappeared with no explanation. Serialise
 * the rep's OWN local midnight instead, so "Tomorrow" means the rep's
 * tomorrow in every zone.
 */
function localMidnightIso(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
}

/** A `YYYY-MM-DD` from the native date input → that LOCAL calendar day's midnight, as an instant. Never `new Date(str)`, which parses as UTC. */
function localMidnightIsoFromDateInput(value: string): string | undefined {
  const day = parseBareCalendarDay(value);
  return day ? localMidnightIso(day) : undefined;
}

const OPTION_BUTTON_CLASS =
  'w-full rounded-md border border-secondary px-2 py-1.5 text-left text-[12px] text-content-primary hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-50 dark:border-nightshade-700 dark:text-nightshade-100 dark:hover:bg-nightshade-700';

export function SnoozePopover({ row, anchorRef, onClose, onActed }: SnoozePopoverProps) {
  // `onActed` rides the HOOK's `onSuccess` (round-1 fix D-J) so it fires on
  // the Mutation itself; only `onClose` and the inline `onError` stay on the
  // per-`mutate()` options, which TanStack v5 drops once this popover's own
  // observer unsubscribes.
  const snooze = useSnoozeSignal({ onActed });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedDate, setPickedDate] = useState('');
  const [error, setError] = useState<InlineError | null>(null);

  // "Indefinitely" renders ONLY when the row carries a full Gong Engage Flow
  // step signature — a row with no step data can never resolve what
  // "comes back when Gong moves to the next step" would even mean.
  const showIndefinitely = hasSnoozeStepSignature(row);

  const isTerminal = error?.code === 'archived' || error?.code === 'closed';
  const showDatePicker = pickerOpen || error?.code === 'needs_date';
  const today = todayLocalMidnight();
  const tomorrow = isoDateOnly(addDays(today, 1));

  function fire(snoozeUntil: string | undefined) {
    setError(null);
    snooze.mutate(
      {
        signalId: row.signal_id,
        snoozeUntil,
        signalType: row.signal_type,
        stepType: row.signal_tactic,
        stepNumber: row.step_number ?? null,
      },
      {
        // The `Action Marked Completed` event is emitted from the hook-level
        // `onSuccess` (it must survive this popover unmounting); only
        // presentation lives here.
        onSuccess: () => onClose(),
        onError: err => {
          if (err.code === 'needs_date') {
            setPickerOpen(true);
          }
          // `err.title` is optional on `ApiProblemError` (unset for a
          // network/aborted failure) — fall back to `err.message`, which is
          // always a rep-safe sentence (plan U4a).
          setError({ code: err.code, title: err.title ?? err.message, detail: err.detail, oauthUrl: err.oauthUrl });
        },
      },
    );
  }

  /**
   * The picked-date path. An unparseable value never falls through to
   * `fire(undefined)` — that means "Indefinitely" on the wire, a completely
   * different (and silently wrong) mark-as-read than the one the rep asked
   * for. It gets the same inline treatment a server refusal does.
   */
  function firePicked() {
    const instant = localMidnightIsoFromDateInput(pickedDate);
    if (!instant) {
      setError({ code: undefined, title: 'Pick a date first.', detail: undefined, oauthUrl: undefined });
      return;
    }
    fire(instant);
  }

  const disabled = isTerminal || snooze.isPending;

  return (
    <ResponsivePopover open onClose={onClose} anchorRef={anchorRef} ariaLabel="Mark this signal as read" className="w-[270px]">
      <div className="p-2.5">
        <div className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">
          Mark as read
        </div>
        <p className="mb-2 text-[11px] text-content-tertiary dark:text-nightshade-500">
          Hides this task for everyone until it comes back, or an external system marks it as completed.
        </p>

        {error && (
          <div data-testid="snooze-error" className="mb-2 text-[11.5px] text-content-danger dark:text-red-400">
            <div className="font-medium">{error.title}</div>
            {error.detail && <div>{error.detail}</div>}
            {error.oauthUrl && (
              <a href={error.oauthUrl} className="font-semibold underline">
                Reconnect Salesforce
              </a>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          {showIndefinitely && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => fire(undefined)}
              aria-label="Indefinitely"
              className={OPTION_BUTTON_CLASS}
            >
              <div>Indefinitely</div>
              <div className="text-[10px] font-normal text-content-tertiary dark:text-nightshade-500">
                Comes back when Gong moves to the next step
              </div>
            </button>
          )}
          <button type="button" disabled={disabled} onClick={() => fire(localMidnightIso(addDays(today, 1)))} className={OPTION_BUTTON_CLASS}>
            Tomorrow
          </button>
          <button type="button" disabled={disabled} onClick={() => fire(localMidnightIso(addDays(today, 3)))} className={OPTION_BUTTON_CLASS}>
            In 3 days
          </button>
          <button type="button" disabled={disabled} onClick={() => fire(localMidnightIso(addDays(today, 7)))} className={OPTION_BUTTON_CLASS}>
            Next week
          </button>
          <button type="button" disabled={disabled} onClick={() => setPickerOpen(true)} className={OPTION_BUTTON_CLASS}>
            Pick a date
          </button>
        </div>

        {showDatePicker && (
          <div className="mt-2">
            <label className="mb-1 block text-[11px] text-content-tertiary dark:text-nightshade-500" htmlFor="snooze-pick-date">
              Mark as read until
            </label>
            <input
              {...AUTOFILL_IGNORE_PROPS}
              id="snooze-pick-date"
              type="date"
              min={tomorrow}
              value={pickedDate}
              disabled={isTerminal}
              onChange={e => setPickedDate(e.target.value)}
              className="w-full rounded-md border border-secondary bg-white px-2 py-1 text-[12px] text-content-primary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
            />
            <button
              type="button"
              disabled={disabled || !pickedDate}
              onClick={firePicked}
              className="mt-1.5 w-full rounded-md bg-content-action px-2 py-1.5 text-[12px] font-medium text-white hover:bg-content-action-hover disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blueberry-500"
            >
              {snooze.isPending ? 'Marking as read…' : 'Mark as read'}
            </button>
          </div>
        )}
      </div>
    </ResponsivePopover>
  );
}
